import { useRef, useState } from 'react';
import { ImagePlus, Trash2, X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useDeleteWineImage, useUpdateWineImage, useUploadWineImage } from './useWineImages';
import type { WineImage } from './types';

const KIND_LABELS: Record<WineImage['kind'], string> = {
  front: 'Frente',
  back: 'Dorso',
  side: 'Lateral',
  cap: 'Cápsula',
  other: 'Otra',
};

const KIND_ORDER: WineImage['kind'][] = ['front', 'back', 'side', 'cap', 'other'];

export function WineImageGallery({ wineId, images }: { wineId: number; images: WineImage[] }) {
  const upload = useUploadWineImage();
  const update = useUpdateWineImage();
  const remove = useDeleteWineImage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightbox, setLightbox] = useState<WineImage | null>(null);

  // Ordenar por kind (front, back, side, cap, other) y luego por position.
  const ordered = [...images].sort((a, b) => {
    const ka = KIND_ORDER.indexOf(a.kind);
    const kb = KIND_ORDER.indexOf(b.kind);
    if (ka !== kb) return ka - kb;
    return a.position - b.position;
  });

  const onPick = () => fileInputRef.current?.click();

  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      await upload.mutateAsync({ wineId, file, kind: 'other' });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="card p-4 md:p-5 space-y-3 lg:col-span-3">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">Imágenes</h2>
        <button type="button" onClick={onPick} className="btn-secondary text-xs">
          <ImagePlus size={14} /> Subir
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onFiles}
        />
      </div>

      {upload.isPending && (
        <p className="text-xs text-notion-500">Subiendo…</p>
      )}

      {ordered.length === 0 ? (
        <div className="text-center text-notion-500 text-sm py-6 border border-dashed border-notion-200 rounded">
          Sin imágenes todavía. Subí frente, dorso, lateral o cápsula.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {ordered.map((img) => (
            <div
              key={img.id}
              className="group relative rounded border border-notion-200 overflow-hidden bg-notion-50"
            >
              <button
                type="button"
                onClick={() => setLightbox(img)}
                className="block w-full aspect-square"
                aria-label={`Ver imagen ${KIND_LABELS[img.kind]}`}
              >
                <img
                  src={img.url}
                  alt={img.original_name ?? ''}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </button>

              <div className="absolute top-1 left-1">
                <select
                  className="text-[10px] bg-white/90 border border-notion-200 rounded px-1 py-0.5 cursor-pointer"
                  value={img.kind}
                  onChange={(e) =>
                    update.mutate({ wineId, imageId: img.id, patch: { kind: e.target.value } })
                  }
                >
                  {KIND_ORDER.map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (confirm('¿Eliminar esta imagen?')) {
                    remove.mutate({ wineId, imageId: img.id });
                  }
                }}
                className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white text-notion-500 hover:text-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Eliminar"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox simple */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white p-2"
            onClick={() => setLightbox(null)}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.original_name ?? ''}
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p
            className={cn(
              'absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs',
              'bg-black/60 px-3 py-1 rounded-full',
            )}
          >
            {KIND_LABELS[lightbox.kind]} · {lightbox.original_name}
          </p>
        </div>
      )}
    </div>
  );
}
